import type { ManualCampaignForm } from '@/features/campaigns/types/campaign';
import {
  todayIsoDate,
  tomorrowIsoDate,
  addDaysIso,
  CAMPAIGN_DATE_MAX_DAYS,
  validateCampaignDateRange,
} from '@/features/campaigns/utils/campaignUtils';
import {
  FieldError,
  FieldLabel,
  campInputClass,
} from '@/features/campaigns/components/FormPrimitives';
import { useInstantAdAccess } from '@/features/profile/hooks/useInstantAdAccess';

type DateRangeFieldsProps = {
  campaignData: ManualCampaignForm;
  setCampaignData: React.Dispatch<React.SetStateAction<ManualCampaignForm>>;
  errors?: Record<string, string>;
  clearError?: (key: string) => void;
  allowToday?: boolean;
};

export function DateRangeFields({
  campaignData,
  setCampaignData,
  errors = {},
  clearError,
  allowToday = false,
}: DateRangeFieldsProps) {
  const instantAdAccess = useInstantAdAccess();
  const minStart =
    allowToday || instantAdAccess ? todayIsoDate() : tomorrowIsoDate();
  const maxStart = addDaysIso(minStart, CAMPAIGN_DATE_MAX_DAYS);
  const minEnd = campaignData.startDate || minStart;
  const maxEnd = campaignData.startDate
    ? addDaysIso(campaignData.startDate, CAMPAIGN_DATE_MAX_DAYS)
    : addDaysIso(minStart, CAMPAIGN_DATE_MAX_DAYS);

  // Live checks for typed values outside min/max (required messages stay on submit).
  const rangeErrors = validateCampaignDateRange(
    campaignData.startDate,
    campaignData.endDate,
    minStart,
  );
  const startError =
    errors.startDate ||
    (campaignData.startDate ? rangeErrors.startDate : '') ||
    '';
  const endError =
    errors.endDate ||
    (campaignData.endDate ? rangeErrors.endDate : '') ||
    '';

  return (
    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-1.5 text-left">
        <FieldLabel required hint={`Earliest ${minStart}`}>
          Start date
        </FieldLabel>
        <input
          type="date"
          min={minStart}
          max={maxStart}
          value={campaignData.startDate}
          aria-invalid={Boolean(startError)}
          onChange={(e) => {
            const startDate = e.target.value;
            clearError?.('startDate');
            clearError?.('endDate');
            setCampaignData((prev) => {
              const endDate =
                prev.endDate && prev.endDate < startDate ? '' : prev.endDate;
              return { ...prev, startDate, endDate };
            });
          }}
          className={`camp-date-input ${campInputClass} ${
            startError ? 'is-invalid' : ''
          }`}
        />
        <FieldError message={startError} />
      </div>
      <div className="flex min-w-0 flex-col gap-1.5 text-left">
        <FieldLabel
          required
          hint={
            campaignData.startDate
              ? `End date must be after ${campaignData.startDate} or later`
              : 'Pick a start date first'
          }
        >
          End date
        </FieldLabel>
        <input
          type="date"
          min={minEnd}
          max={maxEnd}
          value={campaignData.endDate}
          disabled={!campaignData.startDate}
          aria-invalid={Boolean(endError)}
          onChange={(e) => {
            clearError?.('endDate');
            setCampaignData((prev) => ({ ...prev, endDate: e.target.value }));
          }}
          className={`camp-date-input ${campInputClass} disabled:cursor-not-allowed disabled:opacity-50 ${
            endError ? 'is-invalid' : ''
          }`}
        />
        <FieldError message={endError} />
      </div>
    </div>
  );
}
